import { ActivityTrackSample, GpxData } from "../types/trail";

export function parseGPXFile(gpxContent: string): GpxData | null {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(gpxContent, "text/xml");

    // Check for parsing errors
    if (xmlDoc.querySelector("parsererror")) {
      console.error("Error parsing GPX file");
      return null;
    }

    const localNameElements = (element: Element, name: string) =>
      Array.from(element.getElementsByTagName("*"))
        .filter((candidate) => candidate.localName.toLowerCase() === name.toLowerCase());
    const numberFrom = (element: Element | undefined): number | undefined => {
      if (!element?.textContent) return undefined;
      const value = Number(element.textContent.trim());
      return Number.isFinite(value) ? value : undefined;
    };

    const trkpts = localNameElements(xmlDoc.documentElement, "trkpt");
    
    if (trkpts.length === 0) {
      console.error("No track points found in GPX file");
      return null;
    }

    const samples: (ActivityTrackSample & { coordinate: [number, number] })[] = trkpts.flatMap((trkpt) => {
      const latitude = Number(trkpt.getAttribute("lat"));
      const longitude = Number(trkpt.getAttribute("lon"));
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude) ||
          latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return [];

      const rawTimestamp = localNameElements(trkpt, "time")[0]?.textContent?.trim();
      const timestamp = rawTimestamp && !Number.isNaN(Date.parse(rawTimestamp))
        ? new Date(rawTimestamp).toISOString()
        : undefined;
      const extension = localNameElements(trkpt, "extensions")[0] || trkpt;
      const heartRate = numberFrom(localNameElements(extension, "hr")[0]);
      const speed = numberFrom(localNameElements(extension, "speed")[0]);
      const elevation = numberFrom(localNameElements(trkpt, "ele")[0]);

      return [{
        coordinate: [latitude, longitude] as [number, number],
        ...(timestamp ? { timestamp } : {}),
        ...(elevation !== undefined ? { elevation } : {}),
        ...(speed !== undefined ? { speed } : {}),
        ...(heartRate !== undefined ? { heartRate } : {}),
      }];
    });

    const path = samples.map(({ coordinate }) => coordinate);
    for (let index = 1; index < samples.length; index += 1) {
      const previous = samples[index - 1];
      const current = samples[index];
      if (current.speed === undefined && previous.timestamp && current.timestamp) {
        const seconds = (Date.parse(current.timestamp) - Date.parse(previous.timestamp)) / 1000;
        if (seconds > 0) current.speed = calculateDistance(previous.coordinate, current.coordinate) / seconds;
      }
      if (current.speed !== undefined && current.speed > 0) current.pace = 1000 / current.speed / 60;
    }
    const timestamps = samples.every(({ timestamp }) => timestamp)
      ? samples.map(({ timestamp }) => new Date(timestamp as string))
      : undefined;

    // Calculate distance using Haversine formula
    const distance = calculateTotalDistance(path);

    // Calculate center point
    const avgLat = path.reduce((sum, point) => sum + point[0], 0) / path.length;
    const avgLon = path.reduce((sum, point) => sum + point[1], 0) / path.length;
    const center: [number, number] = [avgLat, avgLon];

    // Calculate elevation change and sensor summaries from aligned samples.
    let elevationGain = 0;
    let elevationLoss = 0;
    for (let i = 1; i < samples.length; i++) {
      const previousElevation = samples[i - 1].elevation;
      const currentElevation = samples[i].elevation;
      if (previousElevation === undefined || currentElevation === undefined) continue;
      const change = currentElevation - previousElevation;
      if (change > 0) elevationGain += change;
      else elevationLoss -= change;
    }

    const timedSamples = samples.filter(({ timestamp }) => timestamp);
    const duration = timedSamples.length >= 2
      ? Math.round((Date.parse(timedSamples[timedSamples.length - 1].timestamp as string) - Date.parse(timedSamples[0].timestamp as string)) / 1000)
      : undefined;
    const averageSpeed = duration && distance > 0 ? distance / duration : undefined;
    const speeds = samples.flatMap(({ speed }) => speed === undefined || speed <= 0 ? [] : [speed]);
    const heartRates = samples.flatMap(({ heartRate }) => heartRate === undefined ? [] : [heartRate]);
    const paces = samples.flatMap(({ pace }) => pace === undefined || pace <= 0 ? [] : [pace]);
    const firstTimestamp = timedSamples[0]?.timestamp;

    const startPoint: [number, number] = path[0];
    const endPoint: [number, number] = path[path.length - 1];

    return {
      path,
      distance: Math.round(distance),
      center,
      elevationGain: Math.round(elevationGain),
      elevationLoss: Math.round(elevationLoss),
      duration,
      ...(firstTimestamp ? { date: firstTimestamp } : {}),
      ...(averageSpeed !== undefined ? { avgSpeed: Math.round(averageSpeed * 36) / 10 } : {}),
      ...(speeds.length ? { maxSpeed: Math.round(Math.max(...speeds) * 36) / 10 } : {}),
      ...(averageSpeed !== undefined ? { avgPace: Math.round(1000 / averageSpeed / 60 * 10) / 10 } : {}),
      ...(paces.length ? { maxPace: Math.round(Math.min(...paces) * 10) / 10 } : {}),
      ...(heartRates.length ? { avgHeartRate: Math.round(heartRates.reduce((sum, value) => sum + value, 0) / heartRates.length) } : {}),
      ...(heartRates.length ? { maxHeartRate: Math.max(...heartRates) } : {}),
      startPoint,
      endPoint,
      timestamps,
      samples,
    };
  } catch (error) {
    console.error("Error parsing GPX file:", error);
    return null;
  }
}

function calculateTotalDistance(points: [number, number][]): number {
  if (points.length < 2) return 0;

  let totalDistance = 0;
  for (let i = 0; i < points.length - 1; i++) {
    totalDistance += calculateDistance(points[i], points[i + 1]);
  }
  return totalDistance;
}

function calculateDistance(point1: [number, number], point2: [number, number]): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (point1[0] * Math.PI) / 180;
  const φ2 = (point2[0] * Math.PI) / 180;
  const Δφ = ((point2[0] - point1[0]) * Math.PI) / 180;
  const Δλ = ((point2[1] - point1[1]) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

// Convert GPX data to GeoJSON format for storage
export function gpxToGeoJSON(gpxData: GpxData) {
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: gpxData.path.map(([lat, lon]) => [lon, lat]), // GeoJSON uses [lon, lat]
        },
        properties: {
          distance: gpxData.distance,
          elevationGain: gpxData.elevationGain,
          duration: gpxData.duration,
        },
      },
    ],
  };
}

// Convert GeoJSON back to path format
export function geoJSONToPath(geoJSON: any): [number, number][] {
  try {
    if (geoJSON.features && geoJSON.features.length > 0) {
      const coordinates = geoJSON.features[0].geometry.coordinates;
      // Convert [lon, lat] to [lat, lon]
      return coordinates.map(([lon, lat]: [number, number]) => [lat, lon]);
    }
    return [];
  } catch (error) {
    console.error("Error converting GeoJSON to path:", error);
    return [];
  }
}

// Format duration for display
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  } else if (minutes > 0) {
    return `${minutes}min ${secs}s`;
  } else {
    return `${secs}s`;
  }
}

// Format distance for display
export function formatDistance(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} km`;
  }
  return `${Math.round(meters)} m`;
}
